import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import {
  archiveSinohehReferral,
  getSinohehReferrals,
  updateSinohehReferral,
} from '../../services/sinohehReferralService';

const SAFE_ERROR_MESSAGE = 'عملیات انجام نشد. لطفاً دوباره تلاش کنید.';

const priorityLabels = {
  emergency: 'اورژانسی',
  high: 'بالا',
  medium: 'متوسط',
  low: 'پایین',
};

const pathwayLabels = {
  general: 'ارزیابی عمومی',
  memory_cognitive: 'حافظه و شناخت',
  stroke_neuro: 'علائم مغزی و سکته',
  cardio_bp: 'قلب و فشار خون',
  falls_bone_joint: 'زمین‌خوردن، استخوان و مفصل',
  diabetes: 'دیابت',
  medication_safety: 'ایمنی داروها',
  urinary_infection_incontinence: 'ادراری و بی‌اختیاری',
  frailty_dizziness_weakness: 'ضعف، سرگیجه و بی‌حالی',
  mood_sleep: 'خلق و خواب',
  nutrition_hydration: 'تغذیه و آب بدن',
  skin_rash_itching: 'پوست و خارش',
};

const statusLabels = {
  pending: 'در انتظار بررسی',
  reviewed: 'بررسی‌شده',
  booked: 'رزرو شده',
  cancelled: 'لغو شده',
};

const statusOptions = [
  { value: '', label: 'همه وضعیت‌ها' },
  { value: 'pending', label: statusLabels.pending },
  { value: 'reviewed', label: statusLabels.reviewed },
  { value: 'booked', label: statusLabels.booked },
  { value: 'cancelled', label: statusLabels.cancelled },
];

const priorityOptions = [
  { value: '', label: 'همه اولویت‌ها' },
  { value: 'emergency', label: priorityLabels.emergency },
  { value: 'high', label: priorityLabels.high },
  { value: 'medium', label: priorityLabels.medium },
  { value: 'low', label: priorityLabels.low },
];

const toPersianDate = (value) => {
  if (!value) return '---';
  try {
    return new Date(value).toLocaleString('fa-IR');
  } catch {
    return '---';
  }
};

const getReferralId = (referral) => referral?._id || referral?.id || '';

const isReferralArchived = (referral) =>
  Boolean(referral?.retention?.archivedAt);

const getClientName = (client) => client?.name || 'نام ثبت نشده';

const getClientPhone = (client) => client?.phoneNumber || '---';

const getPathwayLabel = (pathway) => pathwayLabels[pathway] || 'نامشخص';

const getPriorityBadgeClass = (priority) => {
  switch (priority) {
    case 'emergency':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'medium':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'low':
      return 'bg-green-100 text-green-800 border-green-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'reviewed':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'booked':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'cancelled':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    default:
      return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

const getRowClassName = (priority) => {
  if (priority === 'emergency') return 'bg-red-50/80 hover:bg-red-100/80';
  if (priority === 'high') return 'bg-orange-50/60 hover:bg-orange-100/60';
  return 'hover:bg-muted/30';
};

const renderList = (items) => {
  if (!Array.isArray(items) || items.length === 0) return '---';
  return (
    <ul className="list-disc list-inside space-y-1">
      {items.map((item, index) => (
        <li key={`${String(item?.questionId || item?.label || item)}-${index}`}>
          {typeof item === 'string'
            ? item
            : [item?.label, item?.severity].filter(Boolean).join(' - ') ||
              JSON.stringify(item)}
        </li>
      ))}
    </ul>
  );
};

const renderAnswer = (answer) => {
  if (answer === null || answer === undefined || answer === '') return '---';
  if (typeof answer === 'boolean') return answer ? 'بله' : 'خیر';
  if (typeof answer === 'object') {
    return (
      answer.label || answer.value || answer.answer || JSON.stringify(answer)
    );
  }
  return String(answer);
};

const SinohehReferrals = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    includeArchived: false,
  });
  const [form, setForm] = useState({
    status: 'pending',
    notes: '',
    service: '',
    provider: '',
    employee: '',
  });

  const selectedReferralId = useMemo(
    () => getReferralId(selectedReferral),
    [selectedReferral],
  );

  const loadReferrals = async () => {
    try {
      setLoading(true);
      const data = await getSinohehReferrals({
        ...filters,
        includeArchived: filters.includeArchived ? 'true' : undefined,
      });
      const nextReferrals = data.referrals || [];
      setReferrals(nextReferrals);
      setSelectedReferral((current) => {
        if (!current) return current;
        return (
          nextReferrals.find(
            (item) => getReferralId(item) === getReferralId(current),
          ) || null
        );
      });
    } catch (error) {
      toast.error(SAFE_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferrals();
  }, [filters.status, filters.priority, filters.includeArchived]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectReferral = (referral) => {
    setSelectedReferral(referral);
    setForm({
      status: referral.status || 'pending',
      notes: referral.notes || '',
      service: referral.service?._id || referral.service || '',
      provider: referral.provider?._id || referral.provider || '',
      employee: referral.employee?._id || referral.employee || '',
    });
  };

  const handleSave = async () => {
    if (saving || archiving) return;

    if (!selectedReferralId) {
      toast.error('لطفاً ابتدا یک ارجاع را انتخاب کنید.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        status: form.status,
        notes: form.notes,
        service: form.service || null,
        provider: form.provider || null,
        employee: form.employee || null,
      };

      const data = await updateSinohehReferral(selectedReferralId, payload);
      const updatedReferral = data.referral;
      setSelectedReferral(updatedReferral);
      setReferrals((prev) =>
        prev.map((item) =>
          getReferralId(item) === getReferralId(updatedReferral)
            ? updatedReferral
            : item,
        ),
      );
      toast.success('ارجاع با موفقیت ذخیره شد');
    } catch (error) {
      toast.error(SAFE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (saving || archiving) return;

    if (!selectedReferralId) {
      toast.error('لطفاً ابتدا یک ارجاع را انتخاب کنید.');
      return;
    }

    if (isReferralArchived(selectedReferral)) return;

    const confirmed = window.confirm(
      'آیا از بایگانی این ارجاع مطمئن هستید؟ این عملیات حذف دائمی نیست.',
    );

    if (!confirmed) return;

    try {
      setArchiving(true);
      const data = await archiveSinohehReferral(selectedReferralId);
      const archivedReferral = data.referral;

      if (filters.includeArchived) {
        setSelectedReferral(archivedReferral);
        setReferrals((prev) =>
          prev.map((item) =>
            getReferralId(item) === getReferralId(archivedReferral)
              ? archivedReferral
              : item,
          ),
        );
      } else {
        setSelectedReferral(null);
        setReferrals((prev) =>
          prev.filter((item) => getReferralId(item) !== selectedReferralId),
        );
      }

      toast.success('ارجاع بایگانی شد');
    } catch (error) {
      toast.error(SAFE_ERROR_MESSAGE);
    } finally {
      setArchiving(false);
    }
  };

  const triageResult = selectedReferral?.triageSession?.result || {};
  const triageAnswers = selectedReferral?.triageSession?.answers || [];

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="lg:mr-60 pt-16 p-6">
        <BreadcrumbNavigation />

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Icon name="ClipboardCheck" className="text-primary" />
              مدیریت ارجاع‌های سینوهه
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              این بخش برای مدیریت ارجاع است و جایگزین تصمیم پزشکی یا اورژانس
              نیست.
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
              اطلاعات این بخش می‌تواند شامل داده‌های حساس سلامت باشد. فقط برای
              پیگیری مجاز استفاده شود.
            </p>
          </div>
          <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm">
            تعداد ارجاع‌ها: {referrals.length}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="text-sm font-medium">
              وضعیت
              <select
                value={filters.status}
                onChange={(event) =>
                  handleFilterChange('status', event.target.value)
                }
                className="mt-2 w-full border border-border rounded-lg px-3 py-2 bg-background focus:ring-2 focus:ring-primary outline-none">
                {statusOptions.map((option) => (
                  <option
                    key={option.value || 'all-status'}
                    value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium">
              اولویت
              <select
                value={filters.priority}
                onChange={(event) =>
                  handleFilterChange('priority', event.target.value)
                }
                className="mt-2 w-full border border-border rounded-lg px-3 py-2 bg-background focus:ring-2 focus:ring-primary outline-none">
                {priorityOptions.map((option) => (
                  <option
                    key={option.value || 'all-priority'}
                    value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium flex items-end gap-2 bg-muted/30 rounded-lg px-3 py-2">
              <input
                type="checkbox"
                checked={filters.includeArchived}
                onChange={(event) =>
                  handleFilterChange('includeArchived', event.target.checked)
                }
                className="h-4 w-4"
              />
              <span>نمایش بایگانی‌شده‌ها</span>
            </label>
            <div className="flex items-end">
              <Button
                onClick={loadReferrals}
                disabled={loading}
                loading={loading}
                iconName="RefreshCw"
                className="w-full">
                بروزرسانی
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <section className="xl:col-span-2 bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-right">تاریخ</th>
                    <th className="px-4 py-3 text-right">کاربر</th>
                    <th className="px-4 py-3 text-center">اولویت</th>
                    <th className="px-4 py-3 text-center">وضعیت</th>
                    <th className="px-4 py-3 text-right">دلیل</th>
                    <th className="px-4 py-3 text-center">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-gray-400">
                        در حال دریافت اطلاعات...
                      </td>
                    </tr>
                  ) : referrals.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-gray-400">
                        هیچ ارجاعی یافت نشد
                      </td>
                    </tr>
                  ) : (
                    referrals.map((referral) => (
                      <tr
                        key={getReferralId(referral)}
                        className={`${getRowClassName(referral.priority)} transition-colors`}>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                          {toPersianDate(referral.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold">
                            {getClientName(referral.client)}
                          </div>
                          <div className="dir-ltr text-right text-xs text-muted-foreground font-mono">
                            {getClientPhone(referral.client)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex px-2 py-1 rounded border text-xs font-bold ${getPriorityBadgeClass(referral.priority)}`}>
                            {priorityLabels[referral.priority] ||
                              referral.priority ||
                              '---'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`inline-flex px-2 py-1 rounded border text-xs ${getStatusBadgeClass(referral.status)}`}>
                              {statusLabels[referral.status] ||
                                referral.status ||
                                '---'}
                            </span>
                            {isReferralArchived(referral) && (
                              <span className="inline-flex px-2 py-1 rounded border text-xs bg-slate-100 text-slate-700 border-slate-200">
                                بایگانی‌شده
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <div className="line-clamp-2" title={referral.reason}>
                            {referral.reason || '---'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={loading || saving || archiving}
                            onClick={() => handleSelectReferral(referral)}>
                            جزئیات
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border bg-muted/30 flex items-center justify-between">
              <h2 className="font-bold">جزئیات ارجاع</h2>
              {selectedReferral?.priority === 'emergency' && (
                <span className="text-xs bg-red-100 text-red-700 border border-red-300 rounded px-2 py-1 font-bold">
                  اولویت اورژانسی
                </span>
              )}
            </div>

            {!selectedReferral ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                برای مشاهده و ویرایش، یک ارجاع را انتخاب کنید.
              </div>
            ) : (
              <div className="p-4 space-y-5 max-h-[75vh] overflow-y-auto">
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-xs space-y-2">
                  <div>
                    <span className="text-muted-foreground">شناسه ارجاع: </span>
                    <span className="font-mono dir-ltr">
                      {selectedReferralId}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">مراجع: </span>
                    <span className="font-bold">
                      {getClientName(selectedReferral.client)} -{' '}
                      {getClientPhone(selectedReferral.client)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      مسیر ارزیابی:{' '}
                    </span>
                    <span className="font-bold">
                      {getPathwayLabel(selectedReferral.triageSession?.pathway)}
                    </span>
                  </div>
                </div>

                {selectedReferral.priority === 'emergency' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm font-medium">
                    مورد اورژانسی است؛ مدیریت این ارجاع جایگزین تماس با اورژانس
                    یا مراجعه فوری به مرکز درمانی نیست.
                  </div>
                )}

                {isReferralArchived(selectedReferral) && (
                  <div className="bg-slate-50 border border-slate-200 text-slate-700 rounded-lg p-3 text-sm">
                    این ارجاع در تاریخ{' '}
                    {toPersianDate(selectedReferral.retention?.archivedAt)}
                    بایگانی شده است.
                  </div>
                )}

                <div>
                  <h3 className="font-bold mb-3 border-b pb-2">نتیجه تریاژ</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-muted-foreground">سطح ریسک: </span>
                      <span className="font-bold">
                        {triageResult.finalRiskLevel || triageResult.riskLevel || '---'}
                      </span>
                    </p>
                    <p><span className="text-muted-foreground">ریسک قطعی قواعد: </span>{triageResult.deterministicRiskLevel || '---'}</p>
                    <p><span className="text-muted-foreground">برداشت اولیه: </span>{triageResult.diagnosticImpression || '---'}</p>
                    <p><span className="text-muted-foreground">تخصص پیشنهادی: </span>{triageResult.recommendedSpecialty || '---'}</p>
                    <p>
                      <span className="text-muted-foreground">خلاصه: </span>
                      {triageResult.summary || '---'}
                    </p>
                    <p>
                      <span className="text-muted-foreground">
                        اقدام پیشنهادی:{' '}
                      </span>
                      {triageResult.recommendedAction || '---'}
                    </p>
                    <div>
                      <span className="text-muted-foreground block mb-1">
                        دلایل:
                      </span>
                      {renderList(triageResult.reasons)}
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">
                        علائم خطر:
                      </span>
                      {renderList(triageResult.redFlags)}
                    </div>
                    <div><span className="text-muted-foreground block mb-1">احتمال‌های قابل بررسی:</span>{renderList(triageResult.differentialImpressions)}</div>
                    <div><span className="text-muted-foreground block mb-1">اطلاعات تکمیل‌نشده:</span>{renderList(triageResult.missingCriticalInformation)}</div>
                    <p><span className="text-muted-foreground">خلاصه برای پزشک: </span>{triageResult.summaryForDoctor || '---'}</p>
                    <p className="text-xs text-muted-foreground">پروتکل: {selectedReferral.triageSession?.protocolVersion || 'نسخه قدیمی'} — پاسخ‌دهنده: {selectedReferral.triageSession?.clinicalState?.respondentType === 'caregiver' ? 'همراه بیمار' : 'خود بیمار'}</p>
                    <p className="text-xs text-muted-foreground bg-yellow-50 border border-yellow-100 rounded p-2">
                      {triageResult.disclaimer ||
                        'سینوهه جایگزین پزشک یا اورژانس نیست.'}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold mb-3 border-b pb-2">
                    پاسخ‌های تریاژ
                  </h3>
                  <div className="space-y-2 text-sm">
                    {triageAnswers.length === 0 ? (
                      <p className="text-muted-foreground">
                        پاسخی ثبت نشده است.
                      </p>
                    ) : (
                      triageAnswers.map((item, index) => (
                        <div
                          key={`${item.questionId || 'answer'}-${index}`}
                          className="border border-gray-100 rounded-lg p-2">
                          <div className="font-medium">
                            {item.questionText ||
                              item.questionId ||
                              `سوال ${index + 1}`}
                          </div>
                          <div className="text-primary mt-1">
                            {renderAnswer(item.answer)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="space-y-3 border-t border-border pt-4">
                  <label className="block text-sm font-medium">
                    وضعیت
                    <select
                      value={form.status}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          status: event.target.value,
                        }))
                      }
                      className="mt-2 w-full border border-border rounded-lg px-3 py-2 bg-background focus:ring-2 focus:ring-primary outline-none">
                      {statusOptions
                        .filter((option) => option.value)
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </label>

                  <label className="block text-sm font-medium">
                    یادداشت ادمین
                    <textarea
                      value={form.notes}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          notes: event.target.value,
                        }))
                      }
                      className="mt-2 w-full border border-border rounded-lg p-3 h-24 focus:ring-2 focus:ring-primary outline-none"
                      placeholder="توضیحات پیگیری، تماس یا تصمیم مدیریتی..."
                    />
                  </label>

                  <div className="grid grid-cols-1 gap-3">
                    <label className="block text-sm font-medium">
                      شناسه سرویس
                      <input
                        value={form.service}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            service: event.target.value,
                          }))
                        }
                        className="mt-2 w-full border border-border rounded-lg px-3 py-2 dir-ltr text-left focus:ring-2 focus:ring-primary outline-none"
                        placeholder="ObjectId یا خالی برای پاک کردن"
                      />
                    </label>
                    <label className="block text-sm font-medium">
                      شناسه ارائه‌دهنده
                      <input
                        value={form.provider}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            provider: event.target.value,
                          }))
                        }
                        className="mt-2 w-full border border-border rounded-lg px-3 py-2 dir-ltr text-left focus:ring-2 focus:ring-primary outline-none"
                        placeholder="ObjectId یا خالی برای پاک کردن"
                      />
                    </label>
                    <label className="block text-sm font-medium">
                      شناسه کارمند
                      <input
                        value={form.employee}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            employee: event.target.value,
                          }))
                        }
                        className="mt-2 w-full border border-border rounded-lg px-3 py-2 dir-ltr text-left focus:ring-2 focus:ring-primary outline-none"
                        placeholder="ObjectId یا خالی برای پاک کردن"
                      />
                    </label>
                  </div>

                  <Button
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving || archiving}
                    iconName="Save"
                    className="w-full">
                    ذخیره تغییرات
                  </Button>
                  <Button
                    onClick={handleArchive}
                    loading={archiving}
                    disabled={
                      saving ||
                      archiving ||
                      isReferralArchived(selectedReferral)
                    }
                    variant="outline"
                    iconName="Archive"
                    className="w-full">
                    بایگانی ارجاع
                  </Button>
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default SinohehReferrals;
